import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { ArrowLeft, EnvelopeSimple } from '@phosphor-icons/react';
import { Paper, TextInput, Button, Text, Title, Anchor, Alert, Stack, ThemeIcon } from '@mantine/core';
import Logo from '../components/Logo';
import SEO from '../components/SEO';

const labelStyles = {
  label: { fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-5)', marginBottom: 4 },
};

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch {
      setError(t('auth.forgot.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title={t('auth.forgot.seoTitle')} path="/odzyskaj-haslo" noIndex />
      <div className="min-h-screen bg-[#F6F5F4] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center mb-4">
              <Logo className="h-12" />
            </Link>
            <Title order={2} className="text-gray-900">{t('auth.forgot.title')}</Title>
            <Text size="sm" c="dimmed" mt={4}>{t('auth.forgot.subtitle')}</Text>
          </div>

          <Paper shadow="xs" p="xl" radius="md" withBorder>
            {sent ? (
              <Stack align="center" gap="sm">
                <ThemeIcon size={48} radius="xl" color="green" variant="light">
                  <EnvelopeSimple size={24} weight="duotone" />
                </ThemeIcon>
                <Title order={4}>{t('auth.forgot.sentTitle')}</Title>
                <Text size="sm" c="dimmed" ta="center">
                  {t('auth.forgot.sentText')} <Text span fw={600} c="dark">{email}</Text>.
                </Text>
                <Anchor component={Link} to="/logowanie" size="sm" c="brand" mt="sm">
                  <ArrowLeft size={14} className="inline mr-1" />
                  {t('auth.backToLogin')}
                </Anchor>
              </Stack>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert color="red" variant="light" radius="md">
                    <Text size="xs">{error}</Text>
                  </Alert>
                )}

                <TextInput
                  label={t('auth.emailAddress')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  leftSection={<EnvelopeSimple size={16} />}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  size="sm"
                  styles={labelStyles}
                />

                <Button
                  type="submit"
                  fullWidth
                  color="brand"
                  loading={loading}
                  styles={{ root: { fontWeight: 700, fontSize: 15, height: 42 } }}
                >
                  {t('auth.forgot.submit')}
                </Button>

              </form>
            )}
          </Paper>

          <Button
            component={Link}
            to="/logowanie"
            fullWidth
            variant="subtle"
            color="gray"
            leftSection={<ArrowLeft size={14} />}
            mt="sm"
          >
            {t('auth.backToLogin')}
          </Button>
        </div>
      </div>
    </>
  );
}
